/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/context/ThemeContext';
import useMainAssistantSocket from '@/hooks/useMainAssistantSocket';
import MarkdownRenderer from '@/components/MarkdownRenderer';
import AttachmentMessage from '@/ai-platform/components/chat/AttachmentMessage';
import useVoiceChat from '@/ai-platform/hooks/useVoiceChat';
import stripMarkdown from '@/ai-platform/utils/stripMarkdown';
import { agentApi } from '@/ai-platform/services/api';
import SubAgentEventsTree from '@/ai-platform/components/chat/SubAgentEventsTree';
import { APP_ACTION_SURFACES } from '@/access/surfaces';
import { useActionAccess } from '@/access/actionAccess';
import {
  listMainAssistantSessions,
  listAgentSessions,
  archiveMainAssistantSession,
  unarchiveMainAssistantSession,
  deleteMainAssistantSession,
  listUserAgents,
} from '@/api/api';
import GeminiIcon from '@/assets/Google-gemini-icon.svg';
import { formatStoredTime } from '@/utils/datetime';

const isCancelledToolResult = (result) => !!(
  result && (result.cancelled === true || result.data?.cancelled === true)
);

const MAIN_ASSISTANT_ROUTE_MODE_STORAGE_KEY = 'mainAssistantRouteMode';
const MAIN_ASSISTANT_ROUTE_MODE_OPTIONS = [
  {
    value: 'system',
    label: 'System',
    description: 'Follow the current rollout default for Main Assistant routing.',
  },
  {
    value: 'routed',
    label: 'Routed',
    description: 'Force the new planner-routed Main Assistant path.',
  },
  {
    value: 'classic',
    label: 'Classic',
    description: 'Force the legacy direct Main Assistant path.',
  },
];

const getStoredMainAssistantRouteMode = () => {
  try {
    const storedRouteMode = window.localStorage.getItem(MAIN_ASSISTANT_ROUTE_MODE_STORAGE_KEY);
    if (MAIN_ASSISTANT_ROUTE_MODE_OPTIONS.some((option) => option.value === storedRouteMode)) {
      return storedRouteMode;
    }
  } catch {
    // Ignore storage access failures and fall back to the system default.
  }

  return 'system';
};

const withMainAssistantRouteMode = (routeMode, selectedAgentId, options = {}) => {
  if (selectedAgentId !== null || !routeMode) {
    return options;
  }

  return {
    ...options,
    route_mode: routeMode,
  };
};

/**
 * ToolCallCard — collapsible tool call display
 * Shows tool name + status, expands on click to show arguments and result data
 */
const ToolCallCard = ({ msg, isDark, isMobile, colors }) => {
  const [expanded, setExpanded] = useState(false);

  const isRunning = msg.result === undefined || msg.result === null;
  const isCancelled = isCancelledToolResult(msg.result);
  const isSuccess = msg.success === true;
  const statusColor = isRunning
    ? 'var(--color-warning)'
    : isCancelled
      ? colors.textMuted
    : isSuccess
      ? 'var(--color-success)'
      : 'var(--color-destructive)';
  const statusIcon = isRunning ? '⏳' : isCancelled ? '■' : isSuccess ? '✓' : '✗';
  const statusText = isRunning ? 'Running...' : isCancelled ? 'Stopped' : isSuccess ? 'Completed' : 'Failed';

  // Try to extract a short summary from the result
  const resultSummary = (() => {
    if (!msg.result) return null;
    if (msg.result.error) return msg.result.error;
    if (msg.result.data) {
      const d = msg.result.data;
      if (typeof d === 'string') return d.length > 120 ? d.slice(0, 120) + '...' : d;
      if (d.message) return d.message;
      if (d.name) return `${d.deleted ? 'Deleted' : 'Created'}: ${d.name}`;
    }
    return null;
  })();

  return (
    <div
      style={{
        maxWidth: isMobile ? '100%' : '80%',
        borderRadius: 'var(--radius-lg)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        backgroundColor: isDark
          ? 'color-mix(in srgb, var(--color-info) 10%, var(--color-background-surface))'
          : 'color-mix(in srgb, var(--color-info) 8%, var(--color-background-surface))',
        overflow: 'hidden',
        transition: 'all 0.15s ease',
      }}
    >
      {/* Header — always visible, clickable */}
      <div
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {/* Expand chevron */}
        <span
          style={{
            fontSize: '10px',
            color: colors.textMuted,
            transition: 'transform 0.15s ease',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            display: 'inline-block',
          }}
        >
          ▶
        </span>

        {/* Tool icon + name */}
        <span style={{ fontSize: '13px' }}>🔧</span>
        <span style={{
          fontSize: '13px',
          fontWeight: 'var(--font-weight-medium, 500)',
          color: colors.text,
          flex: 1,
        }}>
          {msg.toolName}
        </span>

        {/* Status badge */}
        <span
          style={{
            fontSize: '11px',
            padding: '1px 8px',
            borderRadius: '10px',
            backgroundColor: `color-mix(in srgb, ${statusColor} 20%, transparent)`,
            color: statusColor,
            fontWeight: 'var(--font-weight-medium, 500)',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {statusIcon} {statusText}
        </span>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div
          style={{
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            padding: '10px 12px',
            fontSize: '12px',
          }}
        >
          {/* Arguments */}
          {msg.toolArgs && Object.keys(msg.toolArgs).length > 0 && (
            <div style={{ marginBottom: msg.result ? '10px' : 0 }}>
              <div style={{
                fontSize: '11px',
                fontWeight: 'var(--font-weight-semibold, 600)',
                color: colors.textMuted,
                marginBottom: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Arguments
              </div>
              <pre
                style={{
                  margin: 0,
                  padding: '8px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--color-background-body)',
                  color: colors.text,
                  fontSize: '11px',
                  overflow: 'auto',
                  maxHeight: '150px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(msg.toolArgs, null, 2)}
              </pre>
            </div>
          )}

          {/* Result */}
          {msg.result && (
            <div>
              <div style={{
                fontSize: '11px',
                fontWeight: 'var(--font-weight-semibold, 600)',
                color: colors.textMuted,
                marginBottom: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                Result
              </div>
              {resultSummary && (
                <div style={{
                  padding: '6px 8px',
                  marginBottom: '4px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: `color-mix(in srgb, ${statusColor} 8%, var(--color-background-body))`,
                  color: isSuccess ? colors.text : isCancelled ? colors.textMuted : colors.destructive,
                  fontSize: '12px',
                }}>
                  {resultSummary}
                </div>
              )}
              <pre
                style={{
                  margin: 0,
                  padding: '8px',
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--color-background-body)',
                  color: colors.text,
                  fontSize: '11px',
                  overflow: 'auto',
                  maxHeight: '200px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(msg.result, null, 2)}
              </pre>
            </div>
          )}

          {/* Running indicator if no result yet */}
          {isRunning && (
            <div style={{
              color: colors.textMuted,
              fontSize: '12px',
              fontStyle: 'italic',
            }}>
              Waiting for result...
            </div>
          )}

          {/* Nested sub-agent events — rendered LAST so it's right under the result */}
          {Array.isArray(msg.subAgentEvents) && msg.subAgentEvents.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <SubAgentEventsTree events={msg.subAgentEvents} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * ToolCallGroup — Collapsible container that groups consecutive tool calls.
 * Shows a summary header "🔧 N tool calls" that toggles the list.
 */
const ToolCallGroup = ({ toolMsgs, isDark, isMobile, colors }) => {
  const [collapsed, setCollapsed] = useState(true);

  const allDone = toolMsgs.every(m => m.result !== undefined && m.result !== null);
  const cancelledCount = toolMsgs.filter(m => isCancelledToolResult(m.result)).length;
  const allCancelled = allDone && cancelledCount === toolMsgs.length;
  const anyFailed = toolMsgs.some(m => m.success === false && !isCancelledToolResult(m.result));
  const activitySummary = !allDone
    ? 'Working with data'
    : allCancelled
      ? 'Activity stopped'
      : anyFailed
        ? 'Some checks need attention'
        : cancelledCount > 0
          ? 'Activity partially stopped'
          : 'Checks complete';
  const headerColor = !allDone
    ? 'var(--color-warning)'
    : allCancelled
      ? colors.textMuted
      : anyFailed
        ? 'var(--color-destructive)'
        : cancelledCount > 0
          ? 'var(--color-warning)'
          : 'var(--color-success)';
  const headerIcon = !allDone ? '⏳' : allCancelled ? '■' : anyFailed ? '⚠️' : cancelledCount > 0 ? '⏸' : '✓';

  return (
    <div
      style={{
        maxWidth: isMobile ? '100%' : '80%',
        borderRadius: 'var(--radius-lg)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        backgroundColor: isDark
          ? 'color-mix(in srgb, var(--color-info) 6%, var(--color-background-surface))'
          : 'color-mix(in srgb, var(--color-info) 4%, var(--color-background-surface))',
        overflow: 'hidden',
      }}
    >
      {/* Group header — always visible, clickable */}
      <div
        onClick={(e) => { e.stopPropagation(); setCollapsed(!collapsed); }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <span
          style={{
            fontSize: '10px',
            color: colors.textMuted,
            transition: 'transform 0.15s ease',
            transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
            display: 'inline-block',
          }}
        >
          ▶
        </span>
        <span style={{ fontSize: '13px' }}>🔧</span>
        <span
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            minWidth: 0,
            flex: 1,
          }}
        >
          <span style={{
            fontSize: '13px',
            fontWeight: 'var(--font-weight-medium, 500)',
            color: colors.text,
          }}>
            Assistant activity
          </span>
          <span style={{
            fontWeight: 'normal',
            color: colors.textMuted,
            fontSize: '12px',
          }}>
            {collapsed ? 'Show technical details' : `${toolMsgs.length} background check${toolMsgs.length !== 1 ? 's' : ''}`}
          </span>
        </span>
        <span
          style={{
            fontSize: '11px',
            padding: '1px 8px',
            borderRadius: '10px',
            backgroundColor: `color-mix(in srgb, ${headerColor} 20%, transparent)`,
            color: headerColor,
            fontWeight: 'var(--font-weight-medium, 500)',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {headerIcon} {activitySummary}
        </span>
      </div>

      {/* Collapsible tool call list */}
      {!collapsed && (
        <div style={{
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          padding: '6px',
        }}>
          {toolMsgs.map(msg => (
            <ToolCallCard
              key={msg.id}
              msg={msg}
              isDark={isDark}
              isMobile={isMobile}
              colors={colors}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Groups consecutive tool_call messages together.
 * Returns an array of items: either a regular message or a { type: 'tool_group', msgs: [...] }.
 */
function groupMessages(messages) {
  const result = [];
  let i = 0;
  while (i < messages.length) {
    if (messages[i].role === 'tool_call') {
      const group = [];
      while (i < messages.length && messages[i].role === 'tool_call') {
        group.push(messages[i]);
        i++;
      }
      result.push({ type: 'tool_group', id: `tg-${group[0].id}`, msgs: group });
    } else {
      result.push(messages[i]);
      i++;
    }
  }
  return result;
}

/**
 * AgentChat - Main Assistant chat component using WebSocket
 * 
 * Features:
 * - Full-screen mode
 * - Draggable window (by header)
 * - Resizable from edges and corners
 * - Enhanced session management with search and grouping
 * - Mobile responsive with touch support
 * - Can be opened from external controls (sidebar)
 */
const AgentChat = ({ isExternalOpen, onExternalOpenChange, hideFloatingButton = false }) => {
  const { isDark } = useTheme();
  const mainAssistantAccess = useActionAccess(APP_ACTION_SURFACES.mainAssistantSend);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileViewportHeight, setMobileViewportHeight] = useState(window.visualViewport?.height || window.innerHeight);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track the actual visible viewport height on mobile (accounts for browser URL bar)
  useEffect(() => {
    if (!isMobile) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setMobileViewportHeight(vv.height);
    vv.addEventListener('resize', update);
    update();
    return () => vv.removeEventListener('resize', update);
  }, [isMobile]);
  
  // WebSocket hook
  const {
    isConnected,
    isConnecting,
    agentInfo,
    currentSessionId,
    currentPreflight,
    messages,
    isAwaitingExecutionStart,
    isTyping,
    assistantActivity,
    isLoadingSession,
    streamingContent,
    error,
    connect,
    disconnect,
    switchAgent,
    sendMessage,
    newSession,
    loadSession,
    stopExecution,
    confirmTool,
    pendingConfirmation,
    clearError,
    setOnSessionTitleUpdate,
    setOnSessionCreated,
    isSwitchingAgent,
  } = useMainAssistantSocket();
  const isBusy = isTyping || isAwaitingExecutionStart;
  
  // File upload state
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [mainAssistantRouteMode, setMainAssistantRouteMode] = useState(getStoredMainAssistantRouteMode);
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(null); // null = Main Assistant
  const [showAgentSelector, setShowAgentSelector] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(MAIN_ASSISTANT_ROUTE_MODE_STORAGE_KEY, mainAssistantRouteMode);
    } catch {
      // Ignore storage access failures.
    }
  }, [mainAssistantRouteMode]);

  const handleFileSelect = useCallback(async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      const uploaded = await Promise.all(
        Array.from(files).map((file) => agentApi.uploadAttachment(file))
      );
      setPendingAttachments((prev) => [...prev, ...uploaded]);
    } catch (err) {
      console.error('File upload failed:', err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, []);

  const handleRemoveAttachment = useCallback(async (attachmentId) => {
    try {
      await agentApi.deleteAttachment(attachmentId);
      setPendingAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      console.error('Failed to remove attachment:', err);
    }
  }, []);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleVoiceRecording = useCallback(async (file) => {
    if (!isConnected || isBusy) return;
    if (!mainAssistantAccess.ensureAccess()) return;

    setIsUploading(true);
    try {
      const attachment = await agentApi.uploadAttachment(file, 'Voice recording', 'AUDIO');
      const messageContent = `**Attached Voice Recording:**\n- ${attachment.original_name} (${formatFileSize(attachment.size)}, ${attachment.attachment_type})${attachment.file_url ? ` [View](${attachment.file_url})` : ''}`;
      sendMessage(
        messageContent,
        null,
        withMainAssistantRouteMode(
          mainAssistantRouteMode,
          selectedAgentId,
          { attachment_ids: [attachment.id] },
        ),
      );
    } catch (err) {
      console.error('Voice recording upload failed:', err);
      throw err;
    } finally {
      setIsUploading(false);
    }
  }, [isBusy, isConnected, mainAssistantAccess, mainAssistantRouteMode, selectedAgentId, sendMessage]);

  // Voice chat
  const prevIsTypingRef = useRef(false);
  const {
    voiceState, isVoiceOn, interimTranscript,
    toggleVoice, speakResponse, stopVoice,
    isRecordingSupported,
  } = useVoiceChat({
    onVoiceRecording: handleVoiceRecording,
  });

  // Auto-speak when assistant finishes typing
  useEffect(() => {
    const wasTyping = prevIsTypingRef.current;
    prevIsTypingRef.current = isTyping;
    if (wasTyping && !isTyping && isVoiceOn) {
      const last = messages.length > 0
        ? [...messages].reverse().find((m) => m.role === 'assistant' && m.content)
        : null;
      if (last) {
        speakResponse(stripMarkdown(last.content));
      } else if (voiceState === 'processing') {
        // Backend finished with no assistant response (e.g. timeout/error)
        // so the voice UI must leave processing state.
        stopVoice();
      }
    }
  }, [isTyping, isVoiceOn, voiceState, messages, speakResponse, stopVoice]);

  // UI State - internal open state
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  
  // Sync with external control if provided
  useEffect(() => {
    if (isExternalOpen !== undefined) {
      setIsOpenInternal(isExternalOpen);
    }
  }, [isExternalOpen]);
  
  const setIsOpen = useCallback((value) => {
    const currentIsOpen = isExternalOpen !== undefined ? isExternalOpen : isOpenInternal;
    const newValue = typeof value === 'function' ? value(currentIsOpen) : value;
    setIsOpenInternal(newValue);
    if (onExternalOpenChange) {
      onExternalOpenChange(newValue);
    }
  }, [onExternalOpenChange, isExternalOpen, isOpenInternal]);
  
  // Read-only isOpen value for conditionals (synced with external control)
  const isOpen = isExternalOpen !== undefined ? isExternalOpen : isOpenInternal;
  
  const [isMinimized, setIsMinimized] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(240);
  const [sessionSearch, setSessionSearch] = useState('');
  
  // Pagination state
  const [hasMoreSessions, setHasMoreSessions] = useState(false);
  const [nextPage, setNextPage] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sessionsListRef = useRef(null);
  
  // Request tracking ref to prevent race conditions when switching agents
  const fetchSessionsRequestIdRef = useRef(0);
  
  // Window state - fullscreen, draggable, resizable
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [position, setPosition] = useState(() => ({
    x: window.innerWidth < 768 ? 0 : 20,
    y: window.innerWidth < 768 ? 0 : 20
  }));
  const [size, setSize] = useState(() => ({
    width: window.innerWidth < 768 ? window.innerWidth : 800,
    height: window.innerWidth < 768 ? window.innerHeight : 600
  }));
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  
  // Auto-fullscreen on mobile
  useEffect(() => {
    if (isMobile && isOpen && !isFullscreen) {
      setIsFullscreen(true);
      setSidebarVisible(false);
    }
  }, [isMobile, isOpen]);
  
  // Drag/Resize refs
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dragStartWindowPos = useRef({ x: 0, y: 0 });
  const resizeStartPos = useRef({ x: 0, y: 0 });
  const resizeStartSize = useRef({ width: 0, height: 0 });
  const resizeStartWindowPos = useRef({ x: 0, y: 0 });
  
  // Min/max constraints
  const minSize = { width: 400, height: 300 };
  const minSidebarWidth = 180;
  const maxSidebarWidth = 400;
  
  // Auto-scroll to bottom when new messages arrive
  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMessageCountRef.current = messages.length;
  }, [messages, streamingContent]);
  
  // Connect when chat opens
  useEffect(() => {
    // Don't auto-connect if we're in the middle of switching agents
    if (isOpen && !isConnected && !isConnecting && !isSwitchingAgent()) {
      connect();
    }
  }, [isOpen, isConnected, isConnecting, connect, isSwitchingAgent]);
  
  // Fetch sessions from API based on selected agent
  const fetchSessions = useCallback(async (reset = true) => {
    // Increment request ID to track this specific request
    const requestId = ++fetchSessionsRequestIdRef.current;
    
    if (reset) {
      setSessionsLoading(true);
      setSessions([]);
    }
    try {
      let response;
      const pageSize = 15; // Small page size for fast initial load
      if (selectedAgentId) {
        // Fetch sessions for the specific agent
        response = await listAgentSessions(selectedAgentId, {
          is_active: !showArchived,
          page_size: pageSize,
        });
      } else {
        // Fetch main assistant sessions
        response = await listMainAssistantSessions({
          archived: showArchived,
          page_size: pageSize,
        });
      }
      
      // Check if this is still the latest request (ignore stale responses)
      if (requestId !== fetchSessionsRequestIdRef.current) {
        return;
      }
      
      let sessionsList = response?.results || response?.data?.results || [];
      const nextPageUrl = response?.next || response?.data?.next || null;
      
      // Safety filter: ensure sessions match the current agent selection
      // This prevents displaying sessions from wrong agent due to race conditions
      if (selectedAgentId) {
        // For non-Main Assistant agents, filter by agent_id
        sessionsList = sessionsList.filter(s => s.agent_id === selectedAgentId || s.agent?.id === selectedAgentId);
      } else {
        // For Main Assistant, filter to sessions where agent_type is MAIN_ASSISTANT
        sessionsList = sessionsList.filter(s => {
          const agentType = s.agent_type || s.agent?.agent_type;
          return agentType === 'MAIN_ASSISTANT';
        });
      }
      
      setSessions(sessionsList);
      setHasMoreSessions(!!nextPageUrl);
      setNextPage(nextPageUrl);
    } catch (err) {
      console.error('[AgentChat] Failed to fetch sessions:', err);
    } finally {
      // Only update loading state if this is still the latest request
      if (requestId === fetchSessionsRequestIdRef.current) {
        setSessionsLoading(false);
      }
    }
  }, [showArchived, selectedAgentId]);
  
  // Load more sessions (pagination)
  const loadMoreSessions = useCallback(async () => {
    if (!hasMoreSessions || isLoadingMore || !nextPage) return;
    
    // Track the current agent to detect if agent changed during request
    const requestAgentId = selectedAgentId;
    setIsLoadingMore(true);
    try {
      // Extract page number from nextPage URL
      const url = new URL(nextPage);
      const page = url.searchParams.get('page') || 2;
      
      let response;
      const pageSize = 15;
      if (selectedAgentId) {
        response = await listAgentSessions(selectedAgentId, {
          is_active: !showArchived,
          page_size: pageSize,
          page: page,
        });
      } else {
        response = await listMainAssistantSessions({
          archived: showArchived,
          page_size: pageSize,
          page: page,
        });
      }
      
      // Check if agent changed during request - ignore stale response
      if (requestAgentId !== selectedAgentId) {
        return;
      }
      
      let newSessions = response?.results || response?.data?.results || [];
      const nextPageUrl = response?.next || response?.data?.next || null;
      
      // Safety filter: ensure sessions match the current agent selection
      if (selectedAgentId) {
        newSessions = newSessions.filter(s => s.agent_id === selectedAgentId || s.agent?.id === selectedAgentId);
      } else {
        newSessions = newSessions.filter(s => {
          const agentType = s.agent_type || s.agent?.agent_type;
          return agentType === 'MAIN_ASSISTANT';
        });
      }
      
      
      setSessions(prev => [...prev, ...newSessions]);
      setHasMoreSessions(!!nextPageUrl);
      setNextPage(nextPageUrl);
    } catch (err) {
      console.error('[AgentChat] Failed to load more sessions:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMoreSessions, isLoadingMore, nextPage, selectedAgentId, showArchived]);
  
  // Handle scroll in sessions list for infinite scroll
  const handleSessionsScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // Load more when user scrolls near the bottom (within 100px)
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMoreSessions();
    }
  }, [loadMoreSessions]);
  
  // Clear sessions immediately when agent changes (before fetch completes)
  useEffect(() => {
    setSessions([]);
    setHasMoreSessions(false);
    setNextPage(null);
  }, [selectedAgentId]);
  
  // Fetch sessions when connected or agent changes
  useEffect(() => {
    if (isConnected) {
      fetchSessions();
    }
  }, [isConnected, showArchived, selectedAgentId, fetchSessions]);
  
  // Fetch available agents when chat opens
  useEffect(() => {
    if (isOpen) {
      listUserAgents({ is_active: true })
        .then(response => {
          setAgents(response.results || response || []);
        })
        .catch(err => {
          console.error('[AgentChat] Failed to fetch agents:', err);
        });
    }
  }, [isOpen]);
  
  // Set up session title update callback
  useEffect(() => {
    setOnSessionTitleUpdate((sessionId, title) => {
      setSessions(prev => prev.map(s => 
        s.id === sessionId ? { ...s, title } : s
      ));
    });
  }, [setOnSessionTitleUpdate]);

  // Set up session created callback to refresh session list
  useEffect(() => {
    setOnSessionCreated(() => {
      // Refresh the session list when a new session is created
      fetchSessions();
    });
  }, [setOnSessionCreated, fetchSessions]);
  
  // Initialize position on open
  useEffect(() => {
    if (isOpen && !isFullscreen) {
      setPosition({
        x: window.innerWidth - size.width - 20,
        y: window.innerHeight - size.height - 20,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  
  // Filter and group sessions by date
  const groupedSessions = useMemo(() => {
    const filtered = sessions.filter(s => 
      !sessionSearch || 
      (s.title || 'Untitled Chat').toLowerCase().includes(sessionSearch.toLowerCase())
    );
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    
    const groups = { today: [], yesterday: [], lastWeek: [], lastMonth: [], older: [] };
    
    filtered.forEach(session => {
      const date = new Date(session.last_message_at || session.created_at);
      if (date >= today) groups.today.push(session);
      else if (date >= yesterday) groups.yesterday.push(session);
      else if (date >= lastWeek) groups.lastWeek.push(session);
      else if (date >= lastMonth) groups.lastMonth.push(session);
      else groups.older.push(session);
    });
    
    return groups;
  }, [sessions, sessionSearch]);
  
  // Handle send message
  const handleSend = useCallback(() => {
    if ((!inputValue.trim() && pendingAttachments.length === 0) || isBusy) return;
    if (!mainAssistantAccess.ensureAccess()) return;

    let messageContent = inputValue;
    if (pendingAttachments.length > 0) {
      const fileList = pendingAttachments
        .map((a) => `- ${a.original_name} (${formatFileSize(a.size)}, ${a.attachment_type})${a.file_url ? ` [View](${a.file_url})` : ''}`)
        .join('\n');
      messageContent = messageContent
        ? `${messageContent}\n\n**Attached Files:**\n${fileList}`
        : `**Attached Files:**\n${fileList}`;
    }

    const attachmentIds = pendingAttachments.map((a) => a.id);
    setPendingAttachments([]);
    sendMessage(
      messageContent,
      null,
      withMainAssistantRouteMode(
        mainAssistantRouteMode,
        selectedAgentId,
        { attachment_ids: attachmentIds },
      ),
    );
    setInputValue('');
    inputRef.current?.focus();
  }, [inputValue, isBusy, mainAssistantAccess, mainAssistantRouteMode, pendingAttachments, selectedAgentId, sendMessage]);
  
  // Handle key press in input
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);
  
  // Handle new session
  const handleNewSession = useCallback(() => {
    // Check if current session is already empty (no messages) - don't create another
    if (messages.length === 0 && currentSessionId) {
      return;
    }
    newSession();
    // Clear messages for new session
    // Sessions will be refreshed when session_created event is received
  }, [newSession, messages.length, currentSessionId]);
  
  // Handle session click
  const handleSessionClick = useCallback((sessionId) => {
    if (sessionId !== currentSessionId) {
      loadSession(sessionId);
    }
  }, [currentSessionId, loadSession]);
  
  // Handle archive session
  const handleArchiveSession = useCallback(async (sessionId, e) => {
    e.stopPropagation();
    try {
      if (showArchived) {
        await unarchiveMainAssistantSession(sessionId);
      } else {
        await archiveMainAssistantSession(sessionId);
      }
      fetchSessions();
    } catch (err) {
      console.error('[AgentChat] Failed to archive session:', err);
    }
  }, [showArchived, fetchSessions]);
  
  // Handle delete session
  const handleDeleteSession = useCallback(async (sessionId, e) => {
    e.stopPropagation();
    if (!confirm('Delete this chat permanently?')) return;
    try {
      await deleteMainAssistantSession(sessionId);
      fetchSessions();
    } catch (err) {
      console.error('[AgentChat] Failed to delete session:', err);
    }
  }, [fetchSessions]);
  
  // Drag handlers
  const handleDragStart = useCallback((e) => {
    if (isFullscreen || e.target.tagName === 'BUTTON') return;
    e.preventDefault();
    setIsDragging(true);
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    dragStartWindowPos.current = { ...position };
  }, [isFullscreen, position]);
  
  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;
    const deltaX = e.clientX - dragStartPos.current.x;
    const deltaY = e.clientY - dragStartPos.current.y;
    const newX = Math.max(0, Math.min(window.innerWidth - size.width, dragStartWindowPos.current.x + deltaX));
    const newY = Math.max(0, Math.min(window.innerHeight - size.height, dragStartWindowPos.current.y + deltaY));
    setPosition({ x: newX, y: newY });
  }, [isDragging, size]);
  
  const handleDragEnd = useCallback(() => setIsDragging(false), []);
  
  // Resize handlers
  const handleResizeStart = useCallback((direction) => (e) => {
    if (isFullscreen) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    resizeStartPos.current = { x: e.clientX, y: e.clientY };
    resizeStartSize.current = { ...size };
    resizeStartWindowPos.current = { ...position };
  }, [isFullscreen, size, position]);
  
  const handleResizeMove = useCallback((e) => {
    if (!isResizing || !resizeDirection) return;
    const deltaX = e.clientX - resizeStartPos.current.x;
    const deltaY = e.clientY - resizeStartPos.current.y;
    let newWidth = resizeStartSize.current.width;
    let newHeight = resizeStartSize.current.height;
    let newX = resizeStartWindowPos.current.x;
    let newY = resizeStartWindowPos.current.y;
    
    if (resizeDirection.includes('e')) newWidth = Math.max(minSize.width, resizeStartSize.current.width + deltaX);
    if (resizeDirection.includes('w')) {
      const widthDelta = Math.min(deltaX, resizeStartSize.current.width - minSize.width);
      newWidth = resizeStartSize.current.width - widthDelta;
      newX = resizeStartWindowPos.current.x + widthDelta;
    }
    if (resizeDirection.includes('s')) newHeight = Math.max(minSize.height, resizeStartSize.current.height + deltaY);
    if (resizeDirection.includes('n')) {
      const heightDelta = Math.min(deltaY, resizeStartSize.current.height - minSize.height);
      newHeight = resizeStartSize.current.height - heightDelta;
      newY = resizeStartWindowPos.current.y + heightDelta;
    }
    setSize({ width: newWidth, height: newHeight });
    setPosition({ x: newX, y: newY });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResizing, resizeDirection]);
  
  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setResizeDirection(null);
  }, []);
  
  // Sidebar resize handlers
  const handleSidebarResizeStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizingSidebar(true);
    resizeStartPos.current = { x: e.clientX, y: 0 };
  }, []);
  
  const handleSidebarResizeMove = useCallback((e) => {
    if (!isResizingSidebar) return;
    const delta = e.clientX - resizeStartPos.current.x;
    setSidebarWidth(prev => Math.max(minSidebarWidth, Math.min(maxSidebarWidth, prev + delta)));
    resizeStartPos.current = { x: e.clientX, y: 0 };
  }, [isResizingSidebar]);
  
  const handleSidebarResizeEnd = useCallback(() => setIsResizingSidebar(false), []);
  
  // Global mouse event handlers for drag/resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      handleDragMove(e);
      handleResizeMove(e);
      handleSidebarResizeMove(e);
    };
    const handleMouseUp = () => {
      handleDragEnd();
      handleResizeEnd();
      handleSidebarResizeEnd();
    };
    if (isDragging || isResizing || isResizingSidebar) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, isResizingSidebar, handleDragMove, handleResizeMove, handleSidebarResizeMove, handleDragEnd, handleResizeEnd, handleSidebarResizeEnd]);
  
  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => setIsFullscreen(prev => !prev), []);
  
  // Theme colors – resolved from CSS custom properties so the chat
  // follows whichever color-scheme the rest of the site is using.
  const colors = {
    bg: 'var(--color-background-surface)',
    bgSecondary: 'var(--color-background-body)',
    text: 'var(--color-text-primary)',
    textMuted: 'var(--color-text-secondary)',
    border: 'var(--color-border-default)',
    primary: 'var(--color-primary)',
    userBubble: 'var(--color-primary)',
    assistantBubble: 'var(--color-background-surface)',
    input: 'var(--color-background-input)',
    destructive: 'var(--color-destructive)',
    success: 'var(--color-success)',
    warning: 'var(--color-warning)',
  };
  
  // Filtered sessions based on search
  const filteredGroupedSessions = useMemo(() => {
    if (!sessionSearch.trim()) return groupedSessions;
    const search = sessionSearch.toLowerCase();
    const filterGroup = (sessions) => sessions.filter(s => 
      (s.title || '').toLowerCase().includes(search)
    );
    return {
      today: filterGroup(groupedSessions.today),
      yesterday: filterGroup(groupedSessions.yesterday),
      lastWeek: filterGroup(groupedSessions.lastWeek),
      lastMonth: filterGroup(groupedSessions.lastMonth),
      older: filterGroup(groupedSessions.older),
    };
  }, [groupedSessions, sessionSearch]);
  
  // Render session group
  const renderSessionGroup = (title, sessions) => {
    if (!sessions || sessions.length === 0) return null;
    return (
      <div key={title} style={{ marginBottom: '12px' }}>
        <div style={{ 
          fontSize: '11px', 
          fontWeight: 'var(--font-weight-semibold)', 
          color: colors.textMuted, 
          padding: '4px 8px', 
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          {title}
        </div>
        {sessions.map(session => renderSessionItem(session))}
      </div>
    );
  };
  
  // Render session item
  const renderSessionItem = (session) => (
    <div
      key={session.id}
      onClick={() => handleSessionClick(session.id)}
      style={{
        padding: '10px 12px',
        borderRadius: 'var(--radius-lg)',
        cursor: 'pointer',
        marginBottom: '4px',
        backgroundColor: session.id === currentSessionId ? colors.primary + '20' : 'transparent',
        border: session.id === currentSessionId ? `1px solid ${colors.primary}` : '1px solid transparent',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        transition: 'background-color 0.15s',
      }}
      onMouseEnter={(e) => { if (session.id !== currentSessionId) e.currentTarget.style.backgroundColor = colors.border + '40'; }}
      onMouseLeave={(e) => { if (session.id !== currentSessionId) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{
          fontSize: '13px',
          color: colors.text,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontWeight: session.id === currentSessionId ? 'var(--font-weight-medium)' : 'var(--font-weight-normal, 400)',
        }}>
          {session.title || session.first_message_preview || 'Untitled Chat'}
        </div>
        <div style={{ fontSize: '11px', color: colors.textMuted, marginTop: '2px' }}>
          {formatStoredTime(session.last_message_at || session.created_at, { hour: '2-digit', minute: '2-digit' }, '')}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '4px', opacity: 0.6 }} className="session-actions">
        <button
          onClick={(e) => handleArchiveSession(session.id, e)}
          style={{ padding: '4px 6px', border: 'none', background: 'transparent', color: colors.textMuted, cursor: 'pointer', fontSize: '12px', borderRadius: 'var(--radius-sm)' }}
          title={showArchived ? 'Restore' : 'Archive'}
        >
          {showArchived ? '↩' : '📥'}
        </button>
        <button
          onClick={(e) => handleDeleteSession(session.id, e)}
          style={{ padding: '4px 6px', border: 'none', background: 'transparent', color: colors.destructive, cursor: 'pointer', fontSize: '12px', borderRadius: 'var(--radius-sm)' }}
          title="Delete"
        >
          🗑
        </button>
      </div>
    </div>
  );
  
  // Resize handle style helper
  const resizeHandleStyle = (cursor, position) => ({
    position: 'absolute',
    ...position,
    cursor: isFullscreen ? 'default' : cursor,
    zIndex: 10,
  });
  
  if (!isOpen) {
    // If hideFloatingButton is true, don't render anything when closed
    if (hideFloatingButton) {
      return null;
    }
    return (
      <button
        className="chat-toggle-button"
        onClick={() => setIsOpen(true)}
        title="Open Chat"
        style={{
          position: 'fixed',
          bottom: isMobile ? '16px' : '50px',
          right: isMobile ? '16px' : '30px',
          width: isMobile ? '50px' : '60px',
          height: isMobile ? '50px' : '60px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
          border: 'none',
          boxShadow: 'var(--shadow-lg)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
          zIndex: 9998,
        }}
        onMouseEnter={(e) => !isMobile && (e.currentTarget.style.transform = 'scale(1.1)')}
        onMouseLeave={(e) => !isMobile && (e.currentTarget.style.transform = 'scale(1)')}
      >
        <img src={GeminiIcon} alt="Chat" style={{ width: isMobile ? '24px' : '28px', height: isMobile ? '24px' : '28px', filter: 'brightness(10)' }} />
      </button>
    );
  }
  
  // Minimized state - show a small bar at the bottom
  if (isMinimized) {
    return createPortal(
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '280px',
          backgroundColor: colors.bg,
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-lg)',
          border: `1px solid ${colors.border}`,
          zIndex: 9999,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 14px',
            backgroundColor: colors.bgSecondary,
            cursor: 'pointer',
          }}
          onClick={() => setIsMinimized(false)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ 
              width: '32px', 
              height: '32px', 
              borderRadius: 'var(--radius-lg)', 
              background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <img src={GeminiIcon} alt="Assistant" style={{ width: '18px', height: '18px', filter: 'brightness(10)' }} />
            </div>
            <div>
              <div style={{ fontWeight: 'var(--font-weight-semibold)', color: colors.text, fontSize: '14px' }}>
                {selectedAgentId 
                  ? (agents.find(a => a.id === selectedAgentId)?.name || 'Agent')
                  : (agentInfo?.name || 'Paxia')}
              </div>
              <div style={{ fontSize: '11px', color: isConnected ? 'var(--color-success)' : colors.textMuted }}>
                {isConnected ? 'Online' : 'Offline'}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }}
              style={{
                padding: '6px 8px',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: 'transparent',
                color: colors.text,
                cursor: 'pointer',
                fontSize: '14px',
              }}
              title="Expand"
            >
              ▲
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); disconnect(); setIsOpen(false); setIsMinimized(false); }}
              style={{
                padding: '6px 8px',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: 'transparent',
                color: colors.textMuted,
                cursor: 'pointer',
                fontSize: '16px',
              }}
              title="Close"
            >
              ×
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }
  
  return createPortal(
    <div
      style={{
        position: 'fixed',
        ...(isFullscreen ? {
          top: 0,
          left: 0,
          width: '100vw',
          height: isMobile ? `${mobileViewportHeight}px` : '100dvh',
          borderRadius: 0,
        } : isMobile ? {
          top: 0,
          left: 0,
          right: 0,
          width: '100%',
          height: `${mobileViewportHeight}px`,
          borderRadius: 0,
        } : {
          top: `${position.y}px`,
          left: `${position.x}px`,
          width: `${size.width}px`,
          height: `${size.height}px`,
          borderRadius: 'var(--radius-xl)',
        }),
        backgroundColor: colors.bg,
        boxShadow: (isFullscreen || isMobile) ? 'none' : 'var(--shadow-xl)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 9999,
        border: (isFullscreen || isMobile) ? 'none' : `1px solid ${colors.border}`,
        fontFamily: 'var(--font-primary)',
        transition: isDragging || isResizing ? 'none' : 'border-radius var(--transition-base)',
        // Safe area padding for mobile notches
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : 0,
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : 0,
      }}
      onClick={() => setShowAgentSelector(false)}
    >
      {/* Resize handles (only when not fullscreen) */}
      {!isFullscreen && (
        <>
          {/* Corners */}
          <div onMouseDown={handleResizeStart('nw')} style={resizeHandleStyle('nw-resize', { top: 0, left: 0, width: '12px', height: '12px' })} />
          <div onMouseDown={handleResizeStart('ne')} style={resizeHandleStyle('ne-resize', { top: 0, right: 0, width: '12px', height: '12px' })} />
          <div onMouseDown={handleResizeStart('sw')} style={resizeHandleStyle('sw-resize', { bottom: 0, left: 0, width: '12px', height: '12px' })} />
          <div onMouseDown={handleResizeStart('se')} style={resizeHandleStyle('se-resize', { bottom: 0, right: 0, width: '12px', height: '12px' })} />
          {/* Edges */}
          <div onMouseDown={handleResizeStart('n')} style={resizeHandleStyle('n-resize', { top: 0, left: '12px', right: '12px', height: '6px' })} />
          <div onMouseDown={handleResizeStart('s')} style={resizeHandleStyle('s-resize', { bottom: 0, left: '12px', right: '12px', height: '6px' })} />
          <div onMouseDown={handleResizeStart('w')} style={resizeHandleStyle('w-resize', { left: 0, top: '12px', bottom: '12px', width: '6px' })} />
          <div onMouseDown={handleResizeStart('e')} style={resizeHandleStyle('e-resize', { right: 0, top: '12px', bottom: '12px', width: '6px' })} />
        </>
      )}
      
      {/* Header - Draggable */}
      <div
        onMouseDown={!isMobile ? handleDragStart : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: isMobile ? '10px 12px' : '12px 16px',
          borderBottom: `1px solid ${colors.border}`,
          backgroundColor: colors.bgSecondary,
          cursor: isFullscreen || isMobile ? 'default' : 'move',
          userSelect: 'none',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flex: 1, minWidth: 0 }}>
          <div style={{ 
            width: isMobile ? '32px' : '36px', 
            height: isMobile ? '32px' : '36px', 
            borderRadius: isMobile ? 'var(--radius-lg)' : 'var(--radius-xl)', 
            background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <img src={GeminiIcon} alt="Assistant" style={{ width: isMobile ? '18px' : '20px', height: isMobile ? '18px' : '20px', filter: 'brightness(10)' }} />
          </div>
          <div style={{ position: 'relative', minWidth: 0 }}>
            <div 
              onClick={(e) => { e.stopPropagation(); setShowAgentSelector(!showAgentSelector); }}
              style={{ 
                fontWeight: 'var(--font-weight-semibold)', 
                color: colors.text, 
                fontSize: isMobile ? '13px' : '15px', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {selectedAgentId 
                ? (agents.find(a => a.id === selectedAgentId)?.name || 'Agent')
                : (agentInfo?.name || 'Paxia')}
              <span style={{ fontSize: '10px', opacity: 0.6 }}>▼</span>
            </div>
            <div style={{ fontSize: '12px', color: isConnected ? 'var(--color-success)' : colors.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span style={{ 
                width: '6px', 
                height: '6px', 
                borderRadius: '50%', 
                backgroundColor: isConnecting ? 'var(--color-warning)' : isConnected ? 'var(--color-success)' : 'var(--color-destructive)',
              }} />
              {isConnecting ? 'Connecting...' : isConnected ? 'Online' : 'Offline'}
            </div>
            
            {/* Agent Selector Dropdown */}
            {showAgentSelector && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '8px',
                  minWidth: '200px',
                  backgroundColor: colors.bg,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: 'var(--shadow-lg)',
                  zIndex: 100,
                  overflow: 'hidden',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div style={{ padding: '8px', borderBottom: `1px solid ${colors.border}`, fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase' }}>
                  Select Agent
                </div>
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {/* Main Assistant (default) */}
                  <div
                    onClick={() => {
                      if (selectedAgentId !== null) {
                        setSelectedAgentId(null);
                        setShowAgentSelector(false);
                        switchAgent(null);
                      } else {
                        setShowAgentSelector(false);
                      }
                    }}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      backgroundColor: selectedAgentId === null ? colors.primary + '20' : 'transparent',
                      borderLeft: selectedAgentId === null ? `3px solid ${colors.primary}` : '3px solid transparent',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>🤖</span>
                    <div>
                      <div style={{ fontSize: '13px', color: colors.text, fontWeight: selectedAgentId === null ? 'var(--font-weight-medium)' : 'var(--font-weight-normal, 400)' }}>Main Assistant</div>
                      <div style={{ fontSize: '11px', color: colors.textMuted }}>Default</div>
                    </div>
                  </div>
                  
                  {/* Other agents */}
                  {agents.filter(a => a.agent_type !== 'MAIN_ASSISTANT').map(agent => (
                    <div
                      key={agent.id}
                      onClick={() => {
                        if (selectedAgentId !== agent.id) {
                          setSelectedAgentId(agent.id);
                          setShowAgentSelector(false);
                          switchAgent(agent.id);
                        } else {
                          setShowAgentSelector(false);
                        }
                      }}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        backgroundColor: selectedAgentId === agent.id ? colors.primary + '20' : 'transparent',
                        borderLeft: selectedAgentId === agent.id ? `3px solid ${colors.primary}` : '3px solid transparent',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      <span style={{ fontSize: '14px' }}>{agent.agent_type === 'AI_SYSTEM_BUILDER' ? '🔧' : '💬'}</span>
                      <div>
                        <div style={{ fontSize: '13px', color: colors.text, fontWeight: selectedAgentId === agent.id ? 'var(--font-weight-medium)' : 'var(--font-weight-normal, 400)' }}>{agent.name}</div>
                        <div style={{ fontSize: '11px', color: colors.textMuted }}>{agent.agent_type}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: isMobile ? '4px' : '6px', flexShrink: 0 }}>
          <button
            onClick={handleNewSession}
            style={{
              padding: isMobile ? '6px 10px' : 'var(--btn-padding-y) var(--btn-padding-x)',
              borderRadius: 'var(--radius-lg)',
              border: 'none',
              background: `linear-gradient(135deg, var(--color-primary), var(--color-secondary))`,
              color: 'var(--color-text-accent)',
              cursor: 'pointer',
              fontSize: isMobile ? '12px' : 'var(--btn-font-size)',
              fontWeight: 'var(--font-weight-medium)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <span style={{ fontSize: isMobile ? '12px' : '14px' }}>+</span> {isMobile ? '' : 'New Chat'}
          </button>
          <button
            onClick={() => setSidebarVisible(!sidebarVisible)}
            style={{
              padding: isMobile ? '6px' : '8px',
              borderRadius: 'var(--radius-lg)',
              border: `1px solid ${colors.border}`,
              background: sidebarVisible ? colors.border + '40' : 'transparent',
              color: colors.text,
              cursor: 'pointer',
              fontSize: isMobile ? '14px' : '16px',
            }}
            title="Toggle sidebar"
          >
            ☰
          </button>
          <button
            onClick={() => setIsMinimized(true)}
            style={{
              padding: isMobile ? '6px' : '8px',
              borderRadius: 'var(--radius-lg)',
              border: `1px solid ${colors.border}`,
              background: 'transparent',
              color: colors.text,
              cursor: 'pointer',
              fontSize: isMobile ? '12px' : '14px',
            }}
            title="Minimize"
          >
            ─
          </button>
          {!isMobile && (
            <button
              onClick={toggleFullscreen}
              style={{
                padding: '8px',
                borderRadius: 'var(--radius-lg)',
                border: `1px solid ${colors.border}`,
                background: 'transparent',
                color: colors.text,
                cursor: 'pointer',
                fontSize: '14px',
              }}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? '⊙' : '⛶'}
            </button>
          )}
          <button
            onClick={() => { disconnect(); setIsOpen(false); }}
            style={{
              padding: isMobile ? '6px' : '8px',
              borderRadius: 'var(--radius-lg)',
              border: `1px solid ${colors.border}`,
              background: 'transparent',
              color: colors.textMuted,
              cursor: 'pointer',
              fontSize: isMobile ? '16px' : '18px',
              lineHeight: 1,
            }}
            title="Close"
          >
            ×
          </button>
        </div>
      </div>
      
      {/* Main content */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }}>
        {/* Sidebar - slides over on mobile */}
        {sidebarVisible && (
          <>
            {/* Mobile overlay */}
            {isMobile && (
              <div
                onClick={() => setSidebarVisible(false)}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  zIndex: 5,
                }}
              />
            )}
            <div
              style={{
                width: isMobile ? '85vw' : `${sidebarWidth}px`,
                maxWidth: isMobile ? '320px' : 'none',
                minWidth: isMobile ? 'auto' : `${minSidebarWidth}px`,
                borderRight: `1px solid ${colors.border}`,
                backgroundColor: colors.bgSecondary,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: isMobile ? 'absolute' : 'relative',
                top: isMobile ? 0 : 'auto',
                left: isMobile ? 0 : 'auto',
                bottom: isMobile ? 0 : 'auto',
                zIndex: isMobile ? 10 : 'auto',
              }}
            >
            {/* Search and archive toggle */}
            <div style={{ padding: '12px', borderBottom: `1px solid ${colors.border}` }}>
              <input
                type="text"
                value={sessionSearch}
                onChange={(e) => setSessionSearch(e.target.value)}
                placeholder="Search chats..."
                style={{
                  width: '100%',
                  padding: 'var(--input-padding-y) var(--input-padding-x)',
                  borderRadius: 'var(--radius-lg)',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: colors.input,
                  color: colors.text,
                  fontSize: 'var(--input-font-size)',
                  outline: 'none',
                  marginBottom: '8px',
                }}
              />
              <button
                onClick={() => setShowArchived(!showArchived)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: 'var(--radius-md)',
                  border: 'none',
                  background: showArchived ? colors.primary : colors.border + '40',
                  color: showArchived ? 'var(--color-text-accent)' : colors.text,
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'var(--font-weight-medium)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
              >
                {showArchived ? '📂 Showing Archived' : '📁 Show Archived'}
              </button>
            </div>
            
            {/* Sessions list - grouped with scroll pagination */}
            <div 
              ref={sessionsListRef}
              onScroll={handleSessionsScroll}
              style={{ flex: 1, overflowY: 'auto', padding: '8px' }}
            >
              {sessionsLoading ? (
                <div style={{ textAlign: 'center', color: colors.textMuted, padding: '24px' }}>
                  <div style={{ fontSize: '20px', marginBottom: '8px' }}>⏳</div>
                  Loading sessions...
                </div>
              ) : sessions.length === 0 ? (
                <div style={{ textAlign: 'center', color: colors.textMuted, padding: '24px', fontSize: '13px' }}>
                  <div style={{ fontSize: '24px', marginBottom: '8px' }}>💬</div>
                  No {showArchived ? 'archived ' : ''}chats yet
                </div>
              ) : (
                <>
                  {renderSessionGroup('Today', filteredGroupedSessions.today)}
                  {renderSessionGroup('Yesterday', filteredGroupedSessions.yesterday)}
                  {renderSessionGroup('Last 7 Days', filteredGroupedSessions.lastWeek)}
                  {renderSessionGroup('Last 30 Days', filteredGroupedSessions.lastMonth)}
                  {renderSessionGroup('Older', filteredGroupedSessions.older)}
                  
                  {/* Loading more indicator */}
                  {isLoadingMore && (
                    <div style={{ textAlign: 'center', color: colors.textMuted, padding: '12px', fontSize: '12px' }}>
                      Loading more...
                    </div>
                  )}
                  
                  {/* Load more button (fallback for accessibility) */}
                  {hasMoreSessions && !isLoadingMore && (
                    <button
                      onClick={loadMoreSessions}
                      style={{
                        width: '100%',
                        padding: '8px',
                        marginTop: '8px',
                        borderRadius: 'var(--radius-md)',
                        border: `1px solid ${colors.border}`,
                        background: 'transparent',
                        color: colors.textMuted,
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      Load more sessions
                    </button>
                  )}
                </>
              )}
            </div>
            
            {/* Sidebar resize handle */}
            <div
              onMouseDown={handleSidebarResizeStart}
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: '4px',
                cursor: 'ew-resize',
                backgroundColor: 'transparent',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.primary}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            />
          </div>
          </>
        )}
        
        {/* Chat area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {/* Error display */}
            {error && (
              <div
                style={{
                  padding: '12px',
                  backgroundColor: isDark ? 'rgba(220,38,38,0.15)' : '#fee2e2',
                  borderRadius: 'var(--radius-lg)',
                  color: colors.destructive,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>{error}</span>
                <button
                  onClick={clearError}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}
                >
                  ×
                </button>
              </div>
            )}
            
            {/* Loading indicator when loading session */}
            {isLoadingSession && (
              <div style={{ textAlign: 'center', color: colors.textMuted, padding: '32px' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                <div>Loading conversation...</div>
              </div>
            )}

            {!selectedAgentId && currentPreflight && !isLoadingSession && (
              <div
                style={{
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: `1px solid ${currentPreflight.route_type === 'delegate' ? `${colors.primary}55` : colors.border}`,
                  backgroundColor: currentPreflight.route_type === 'delegate'
                    ? (isDark ? 'rgba(59,130,246,0.12)' : 'rgba(219,234,254,0.8)')
                    : colors.bgSecondary,
                  color: colors.text,
                  marginBottom: '12px',
                }}
              >
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '6px',
                  }}
                >
                  Route Preview
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px' }}>
                  {currentPreflight.route_label || 'Handled by Main Assistant'}
                </div>
                {isAwaitingExecutionStart && (
                  <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '10px' }}>
                    Queued. Waiting for the AI worker to start this run.
                  </div>
                )}
                <div
                  style={{
                    display: 'grid',
                    gap: '10px',
                    gridTemplateColumns: isMobile
                      ? `repeat(${currentPreflight.show_estimates_enabled === false ? 1 : 2}, minmax(0, 1fr))`
                      : `repeat(${currentPreflight.show_estimates_enabled === false ? 1 : 4}, minmax(0, 1fr))`,
                  }}
                >
                  {currentPreflight.show_estimates_enabled !== false && (
                    <>
                      <div>
                        <div style={{ fontSize: '11px', color: colors.textMuted }}>
                          {currentPreflight.service_bundle_label ? 'Bundle' : 'Tariff'}
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: 500 }}>
                          {currentPreflight.service_bundle_label || currentPreflight.tariff_class || 'chat_basic'}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: colors.textMuted }}>Estimated credits</div>
                        <div style={{ fontSize: '13px', fontWeight: 500 }}>
                          {currentPreflight.estimated_credits ?? '—'}
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: colors.textMuted }}>Reserve required</div>
                        <div style={{ fontSize: '13px', fontWeight: 500 }}>
                          {currentPreflight.reserve_required ? 'Yes' : 'No'}
                        </div>
                      </div>
                    </>
                  )}
                  <div>
                    <div style={{ fontSize: '11px', color: colors.textMuted }}>
                      {currentPreflight.route_type === 'delegate' ? 'Specialist' : 'Mode'}
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 500 }}>
                      {currentPreflight.route_type === 'delegate'
                        ? (currentPreflight.specialist_label || 'Delegated specialist')
                        : (currentPreflight.execution_mode === 'coordinate_bundle' || currentPreflight.service_bundle_label
                            ? 'Coordinated bundle'
                            : (currentPreflight.routing_mode === 'bypass' ? 'Bypass' : 'Direct'))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Welcome message if no messages */}
            {messages.length === 0 && !isBusy && !isLoadingSession && (
              <div style={{ textAlign: 'center', color: colors.textMuted, padding: '32px' }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>👋</div>
                <div>Hi! How can I assist you today?</div>
              </div>
            )}
            
            {/* Messages */}
            {groupMessages(messages).map((item) => (
              item.type === 'tool_group' ? (
                // Grouped tool calls in a collapsible container
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                  }}
                >
                  <ToolCallGroup
                    toolMsgs={item.msgs}
                    isDark={isDark}
                    isMobile={isMobile}
                    colors={colors}
                  />
                </div>
              ) : (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  justifyContent: item.role === 'user' ? 'flex-end' : 'flex-start',
                }}
              >
                  {/* Regular message */}
                  <div
                    style={{
                      maxWidth: isMobile ? '100%' : '80%',
                      padding: '12px 16px',
                      borderRadius: item.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                      backgroundColor: item.role === 'user' ? colors.userBubble : colors.assistantBubble,
                      color: item.role === 'user' ? 'var(--color-text-accent)' : colors.text,
                      boxShadow: 'var(--shadow-sm)',
                      wordBreak: 'break-word',
                    }}
                  >
                    {item.role === 'user' ? (
                      <AttachmentMessage content={item.content} />
                    ) : (
                      // Assistant messages: render as markdown
                      <MarkdownRenderer content={item.content} />
                    )}
                  </div>
              </div>
              )
            ))}
            
            {/* Pending Confirmation Dialog */}
            {pendingConfirmation && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: isMobile ? '100%' : '80%',
                    padding: '16px',
                    borderRadius: 'var(--radius-lg)',
                    backgroundColor: isDark ? 'color-mix(in srgb, var(--color-warning) 15%, var(--color-background-surface))' : 'color-mix(in srgb, var(--color-warning) 10%, var(--color-background-surface))',
                    border: '1px solid var(--color-warning)',
                    color: colors.text,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <span style={{ fontSize: '18px' }}>⚠️</span>
                    <span style={{ fontWeight: 'var(--font-weight-semibold, 600)' }}>Confirmation Required</span>
                    {pendingConfirmation.dangerLevel > 0 && (
                      <span
                        style={{
                          fontSize: '11px',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          backgroundColor: pendingConfirmation.dangerLevel >= 4 ? 'var(--color-destructive)' : 'var(--color-warning)',
                          color: '#fff',
                          fontWeight: 'var(--font-weight-semibold, 600)',
                        }}
                      >
                        Danger Level {pendingConfirmation.dangerLevel}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                    <strong>{pendingConfirmation.toolName}</strong> wants to execute with:
                  </div>
                  {pendingConfirmation.toolArgs && Object.keys(pendingConfirmation.toolArgs).length > 0 && (
                    <pre
                      style={{
                        fontSize: '11px',
                        padding: '8px',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: 'var(--color-background-body)',
                        marginBottom: '12px',
                        overflow: 'auto',
                        maxHeight: '120px',
                      }}
                    >
                      {JSON.stringify(pendingConfirmation.toolArgs, null, 2)}
                    </pre>
                  )}
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => confirmTool(pendingConfirmation.executionId, pendingConfirmation.toolName, false)}
                      style={{
                        padding: '6px 16px',
                        borderRadius: 'var(--radius-md)',
                        border: '1px solid var(--color-border-default)',
                        backgroundColor: 'transparent',
                        color: colors.text,
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 'var(--font-weight-medium, 500)',
                      }}
                    >
                      Deny
                    </button>
                    <button
                      onClick={() => confirmTool(pendingConfirmation.executionId, pendingConfirmation.toolName, true)}
                      style={{
                        padding: '6px 16px',
                        borderRadius: 'var(--radius-md)',
                        border: 'none',
                        backgroundColor: 'var(--color-warning)',
                        color: '#000',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 'var(--font-weight-semibold, 600)',
                      }}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Streaming content */}
            {(isBusy || streamingContent) && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div
                  style={{
                    maxWidth: isMobile ? '100%' : '80%',
                    padding: '12px 16px',
                    borderRadius: '18px 18px 18px 4px',
                    backgroundColor: colors.assistantBubble,
                    color: colors.text,
                    boxShadow: 'var(--shadow-sm)',
                  }}
                >
                  {streamingContent ? (
                    <MarkdownRenderer content={streamingContent} />
                  ) : assistantActivity ? (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '999px',
                          backgroundColor: colors.primary,
                          marginTop: '5px',
                          flexShrink: 0,
                          animation: 'typingDot 1.4s infinite',
                        }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600 }}>{assistantActivity.message}</div>
                        {assistantActivity.detail && (
                          <div style={{ fontSize: '12px', color: colors.textMuted }}>
                            {assistantActivity.detail}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : isAwaitingExecutionStart ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 600 }}>Queued on AI worker</div>
                      <div style={{ fontSize: '12px', color: colors.textMuted }}>
                        Waiting for the backend worker to start the response.
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <span className="typing-dot">•</span>
                      <span className="typing-dot" style={{ animationDelay: '0.2s' }}>•</span>
                      <span className="typing-dot" style={{ animationDelay: '0.4s' }}>•</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Input area */}
          <div
            style={{
              padding: isMobile ? '10px 12px' : '12px 16px',
              borderTop: `1px solid ${colors.border}`,
              backgroundColor: colors.bgSecondary,
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
              // Safe area for mobile keyboard
              paddingBottom: isMobile ? 'max(10px, env(safe-area-inset-bottom))' : '12px',
            }}
          >
            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              style={{ display: 'none' }}
              accept=".txt,.md,.json,.csv,.py,.js,.ts,.jsx,.tsx,.html,.css,.xml,.yaml,.yml,.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.svg"
            />

            {/* Attachment chips */}
            {pendingAttachments.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px', width: '100%' }}>
                {pendingAttachments.map((att) => (
                  <div key={att.id} style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '4px 10px', borderRadius: '16px',
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.bg, fontSize: '12px', color: colors.text,
                    maxWidth: '240px',
                  }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      📎 {att.original_name}
                    </span>
                    <span style={{ color: colors.textMuted, flexShrink: 0 }}>
                      ({formatFileSize(att.size)})
                    </span>
                    <button
                      onClick={() => handleRemoveAttachment(att.id)}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: colors.textMuted, fontSize: '14px', padding: '0 2px',
                        lineHeight: 1,
                      }}
                      title="Remove"
                    >✕</button>
                  </div>
                ))}
              </div>
            )}

            {/* Voice overlay */}
            {isVoiceOn && voiceState !== 'idle' && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '4px 12px', marginBottom: '6px',
                borderRadius: '12px',
                backgroundColor: voiceState === 'recording' ? 'rgba(239,68,68,0.15)'
                  : voiceState === 'speaking' ? 'rgba(34,197,94,0.15)' : 'rgba(234,179,8,0.15)',
                fontSize: '13px', color: colors.textMuted, width: '100%',
              }}>
                <span>{voiceState === 'recording' ? '🎙️ Recording…' : voiceState === 'speaking' ? '🔊 Speaking…' : '⏳ Processing…'}</span>
                {interimTranscript && <span style={{ fontStyle: 'italic', opacity: 0.7 }}>{interimTranscript}</span>}
              </div>
            )}

            {selectedAgentId === null && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  flexWrap: 'wrap',
                  marginBottom: '8px',
                  width: '100%',
                }}
              >
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: colors.textMuted,
                  }}
                >
                  Path
                </span>
                {MAIN_ASSISTANT_ROUTE_MODE_OPTIONS.map((option) => {
                  const isActive = option.value === mainAssistantRouteMode;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setMainAssistantRouteMode(option.value)}
                      title={option.description}
                      style={{
                        borderRadius: '999px',
                        border: `1px solid ${isActive ? colors.primary : colors.border}`,
                        backgroundColor: isActive ? colors.primary : colors.bg,
                        color: isActive ? '#fff' : colors.text,
                        fontSize: '12px',
                        fontWeight: 600,
                        padding: '6px 10px',
                        cursor: 'pointer',
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            )}

            {!selectedAgentId && currentPreflight && !isLoadingSession && (
              <div
                style={{
                  width: '100%',
                  marginBottom: '8px',
                  padding: isMobile ? '10px 12px' : '12px 14px',
                  borderRadius: '14px',
                  border: `1px solid ${currentPreflight.route_type === 'delegate' ? `${colors.primary}55` : colors.border}`,
                  backgroundColor: currentPreflight.route_type === 'delegate'
                    ? (isDark ? 'rgba(59,130,246,0.12)' : 'rgba(219,234,254,0.8)')
                    : colors.bgSecondary,
                  color: colors.text,
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    flexDirection: isMobile ? 'column' : 'row',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: colors.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '4px',
                      }}
                    >
                      Current Route
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>
                      {currentPreflight.route_label || 'Handled by Main Assistant'}
                    </div>
                    {currentPreflight.service_bundle_label && (
                      <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '2px' }}>
                        {currentPreflight.service_bundle_label}
                        {currentPreflight.line_item_count ? ` · ${currentPreflight.line_item_count} services` : ''}
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap',
                      justifyContent: isMobile ? 'flex-start' : 'flex-end',
                    }}
                  >
                    {currentPreflight.show_estimates_enabled !== false && (
                      <div
                        style={{
                          padding: '6px 10px',
                          borderRadius: '999px',
                          backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.7)',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        {`Est. ${currentPreflight.estimated_credits ?? '—'} cr`}
                      </div>
                    )}
                    <div
                      style={{
                        padding: '6px 10px',
                        borderRadius: '999px',
                        backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.7)',
                        fontSize: '12px',
                        fontWeight: 500,
                      }}
                    >
                      {currentPreflight.reserve_required ? 'Reserve required' : 'No reserve'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: isMobile ? '8px' : '12px', alignItems: 'flex-end', width: '100%' }}>
            {/* Attach file button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || mainAssistantAccess.disabled}
              title={mainAssistantAccess.disabled ? mainAssistantAccess.blockedMessage : 'Attach file'}
              style={{
                width: isMobile ? '40px' : '44px',
                height: isMobile ? '40px' : '44px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: colors.border,
                color: colors.text,
                cursor: isUploading || mainAssistantAccess.disabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '18px' : '20px',
                flexShrink: 0,
                opacity: isUploading || mainAssistantAccess.disabled ? 0.5 : 1,
              }}
            >
              {isUploading ? '⏳' : '📎'}
            </button>

            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={mainAssistantAccess.disabled ? mainAssistantAccess.blockedMessage : isVoiceOn ? 'Recording voice message...' : 'Type your message...'}
              disabled={!isConnected || mainAssistantAccess.disabled}
              style={{
                flex: 1,
                padding: isMobile ? '8px 12px' : '10px 14px',
                borderRadius: '20px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.bg,
                color: colors.text,
                fontSize: isMobile ? '14px' : '14px',
                resize: 'none',
                minHeight: isMobile ? '40px' : '44px',
                maxHeight: '120px',
                outline: 'none',
              }}
              rows={1}
            />

            {/* Mic / voice button */}
            <button
              onClick={toggleVoice}
              disabled={!isRecordingSupported || mainAssistantAccess.disabled}
              title={mainAssistantAccess.disabled ? mainAssistantAccess.blockedMessage : !isRecordingSupported ? 'Voice recording not supported' : voiceState === 'recording' ? 'Send voice recording' : isVoiceOn ? 'Stop voice' : 'Start voice recording'}
              style={{
                width: isMobile ? '40px' : '44px',
                height: isMobile ? '40px' : '44px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: isVoiceOn ? '#ef4444' : colors.border,
                color: '#fff',
                cursor: isRecordingSupported && !mainAssistantAccess.disabled ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: isMobile ? '18px' : '20px',
                flexShrink: 0,
                opacity: isRecordingSupported && !mainAssistantAccess.disabled ? 1 : 0.4,
                animation: voiceState === 'recording' ? 'voicePulse 1.5s ease-in-out infinite' : 'none',
              }}
            >
              {voiceState === 'recording' ? '🎙️' : voiceState === 'speaking' ? '🔊' : voiceState === 'processing' ? '⏳' : '🎤'}
            </button>
            
            {isBusy ? (
              <button
                onClick={stopExecution}
                style={{
                  width: isMobile ? '40px' : '44px',
                  height: isMobile ? '40px' : '44px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: colors.destructive,
                  color: 'var(--color-text-accent)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '14px' : '16px',
                  flexShrink: 0,
                }}
                title={isAwaitingExecutionStart ? 'Cancel queued run' : 'Stop'}
              >
                ■
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={(!inputValue.trim() && pendingAttachments.length === 0) || !isConnected || mainAssistantAccess.disabled}
                style={{
                  width: isMobile ? '40px' : '44px',
                  height: isMobile ? '40px' : '44px',
                  borderRadius: '50%',
                  border: 'none',
                  backgroundColor: (inputValue.trim() || pendingAttachments.length > 0) && isConnected && !mainAssistantAccess.disabled ? colors.primary : colors.border,
                  color: 'var(--color-text-accent)',
                  cursor: (inputValue.trim() || pendingAttachments.length > 0) && isConnected && !mainAssistantAccess.disabled ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isMobile ? '16px' : '18px',
                  flexShrink: 0,
                }}
                title={mainAssistantAccess.disabled ? mainAssistantAccess.blockedMessage : 'Send'}
              >
                ➤
              </button>
            )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Typing animation styles + session action hover */}
      <style>{`
        @keyframes typingDot {
          0%, 60%, 100% { opacity: 0.3; }
          30% { opacity: 1; }
        }
        .typing-dot {
          animation: typingDot 1.4s infinite;
        }
        .session-actions {
          opacity: 0;
          transition: opacity 0.15s;
        }
        div:hover > .session-actions {
          opacity: 1 !important;
        }
        @keyframes voicePulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.5); }
          50% { box-shadow: 0 0 0 10px rgba(239,68,68,0); }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default AgentChat;
